import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import PageContainer from "../../components/PageContainer";
import PageSection from "../../components/PageSection";
import NoticeboardButton from "../../components/shared/NoticeboardButton";
import { HELP_VIDEOS_PATH } from "../../domain/help/helpContent";
import {
  bannerVideo,
  COURSE_LENGTH,
  COURSE_PATH,
  lessonBySlug,
  lessonPath,
  nextLesson,
  previousLesson,
  readMinutes,
  type Lesson,
} from "../../domain/help/course";
import { useCourseProgress } from "../../hooks/useCourseProgress";
import { CourseBreadcrumbs } from "./CourseOverviewParts";
import CourseSidebar from "./CourseSidebar";
import ForAdminsTag from "./ForAdminsTag";
import { routerLinkProps } from "./routerLinkProps";

const Banner: React.FC<{ lesson: Lesson }> = ({ lesson }) => {
  const theme = useTheme();
  const video = bannerVideo(lesson);
  const frame = {
    display: "block",
    width: "100%",
    borderRadius: { xs: 2, md: 3 },
    border: "1px solid",
    borderColor: "divider",
    bgcolor: theme.palette.common.black,
  };
  if (video) {
    return (
      <Box
        component="video"
        controls
        preload="metadata"
        src={video.videoPath}
        poster={video.thumbnail}
        title={video.title}
        sx={{ ...frame, aspectRatio: "16 / 9" }}
      />
    );
  }
  const { banner } = lesson;
  if ("video" in banner) return null;
  return (
    <Box
      component="img"
      src={banner.image}
      alt={banner.alt}
      decoding="async"
      sx={{ ...frame, height: "auto" }}
    />
  );
};

const CourseLesson: React.FC = () => {
  const theme = useTheme();
  const { slug } = useParams();
  const lesson = lessonBySlug(slug);
  // Only Next (Done on the last lesson) finishes a lesson; reading or scrolling doesn't
  const progress = useCourseProgress();
  const { markDone } = progress;

  if (!lesson) return <Navigate to={COURSE_PATH} replace />;
  const previous = previousLesson(lesson);
  const next = nextLesson(lesson);

  return (
    <PageContainer sx={{ maxWidth: { xs: 800, lg: 1280 }, px: { xs: 0, sm: 1 } }}>
      <PageSection sx={{ display: { lg: "grid" }, gridTemplateColumns: "minmax(0, 1fr) 320px", gap: 4 }}>
        <Box component="article" sx={{ minWidth: 0, mt: { xs: 2, md: 1 }, mb: 6 }}>
          <CourseBreadcrumbs
            crumbs={[
              { label: "Help Center", to: HELP_VIDEOS_PATH },
              { label: "Noticeboard Fundamentals", to: COURSE_PATH },
              { label: `Lesson ${lesson.number}` },
            ]}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                color: "primary.main",
                fontWeight: 600,
                fontSize: "0.9rem",
              }}
            >
              Lesson {lesson.number} of {COURSE_LENGTH}
            </Typography>
            {lesson.forAdmins && <ForAdminsTag />}
          </Box>
          <Typography
            component="h1"
            sx={{
              mt: 1,
              fontSize: { xs: "1.9rem", md: "2.4rem" },
              lineHeight: 1.1,
              fontWeight: 400,
              letterSpacing: "-0.015em",
            }}
          >
            {lesson.title}
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: { xs: "1.05rem", md: "1.15rem" },
              color: "text.secondary",
              lineHeight: 1.45,
            }}
          >
            {lesson.intro}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {readMinutes(lesson)} min read
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Banner lesson={lesson} />
          </Box>

          <Box
            component="aside"
            aria-label="The idea"
            sx={{
              mt: 4,
              p: { xs: 2, md: 2.5 },
              display: "flex",
              gap: 1.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.2),
              bgcolor: alpha(theme.palette.primary.main, 0.06),
            }}
          >
            <Box
              aria-hidden
              sx={{
                color: "primary.main",
                fontSize: "1.2rem",
                lineHeight: 1.4,
              }}
            >
              ✦
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                The idea
              </Typography>
              <Typography
                sx={{ mt: 0.5, fontSize: "1.05rem", lineHeight: 1.5 }}
              >
                {lesson.idea}
              </Typography>
            </Box>
          </Box>

          {lesson.sections.map((section) => (
            <Box component="section" key={section.heading} sx={{ mt: 4 }}>
              <Typography
                component="h2"
                variant="h6"
                fontWeight={600}
                sx={{ mb: 1.5 }}
              >
                {section.heading}
              </Typography>
              {section.paragraphs?.map((paragraph) => (
                <Typography key={paragraph} sx={{ mb: 1.5, lineHeight: 1.65 }}>
                  {paragraph}
                </Typography>
              ))}
              {section.steps && (
                <Box
                  component="ol"
                  sx={{
                    pl: 3,
                    my: 0,
                    "& li": { mb: 1, lineHeight: 1.65, pl: 0.5 },
                  }}
                >
                  {section.steps.map((step) => (
                    <Typography component="li" key={step}>
                      {step}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}

          <Box
            component="nav"
            aria-label="Lessons"
            sx={{
              mt: 5,
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
              {previous ? (
                <NoticeboardButton
                  variant="secondary"
                  startIcon={<ArrowBackIcon />}
                  {...routerLinkProps(lessonPath(previous))}
                >
                  Previous
                </NoticeboardButton>
              ) : (
                <span />
              )}
              <NoticeboardButton
                variant="primary"
                endIcon={next ? <ArrowForwardIcon /> : <CheckIcon />}
                {...routerLinkProps(next ? lessonPath(next) : COURSE_PATH)}
                onClick={() => markDone(lesson.slug)}
              >
                {next ? "Next" : "Done"}
              </NoticeboardButton>
          </Box>
        </Box>
        <CourseSidebar lesson={lesson} progress={progress} />
      </PageSection>
    </PageContainer>
  );
};

export default CourseLesson;
